import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase';

interface UserData {
  displayName: string;
  email: string;
  monkeyPoints: number;
  photoURL?: string;
}

export default function UserDashboard() {
  const { t } = useTranslation();
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [userData, setUserData] = useState<UserData | null>(null);
  const [loading, setLoading] = useState(true);

  // Get initials from name
  const getInitials = (name: string) => {
    const words = name.trim().split(' ');
    if (words.length >= 2) {
      // Multiple words: first letter of each word
      return (words[0][0] + words[1][0]).toUpperCase();
    } else {
      // Single word: first two letters
      return name.substring(0, 2).toUpperCase();
    }
  };

  useEffect(() => {
    const fetchUserData = async () => {
      if (!user) return;

      try {
        const userDoc = await getDoc(doc(db, 'users', user.id));
        if (userDoc.exists()) {
          setUserData(userDoc.data() as UserData);
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, [user]);

  const handleLogout = async () => {
    await signOut();
    navigate('/');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-surface-2 flex items-center justify-center">
        <div className="text-4xl">⏳</div>
      </div>
    );
  }

  const dashboardCards = [
    {
      id: 'take-ticket',
      title: t('userDashboard.cards.takeTicket.title'),
      description: t('userDashboard.cards.takeTicket.description'),
      icon: '🎫',
      path: '/take-ticket',
      gradient: 'from-surface-2'
    },
    {
      id: 'my-profile',
      title: t('userDashboard.cards.myProfile.title'),
      description: t('userDashboard.cards.myProfile.description'),
      icon: '👤',
      path: '/user/profile',
      gradient: 'from-surface-2'
    },
    {
      id: 'my-appointments',
      title: t('userDashboard.cards.myAppointments.title'),
      description: t('userDashboard.cards.myAppointments.description'),
      icon: '📅',
      path: '/user/appointments',
      gradient: 'from-surface-2'
    },
    {
      id: 'history',
      title: t('userDashboard.cards.history.title'),
      description: t('userDashboard.cards.history.description'),
      icon: '📜',
      path: '/user/history',
      gradient: 'from-surface-2'
    }
  ];

  return (
    <div className="min-h-screen bg-surface-2 transition-colors duration-300">
      <Helmet>
        <title>{t('userDashboard.seo.title')}</title>
      </Helmet>

      {/* Header */}
      <header className="bg-surface shadow-md border-b border-hairline">
        <div className="container mx-auto px-6 py-6">
          <div className="flex items-center justify-between">
            {/* User Info */}
            <div className="flex items-center gap-4">
              {userData?.photoURL ? (
                <img
                  src={userData.photoURL}
                  alt={userData.displayName}
                  className="w-16 h-16 rounded-full object-cover border-4 border-accent/30/30"
                />
              ) : (
                <div className="w-16 h-16 rounded-full flex items-center justify-center text-3xl font-black text-ink">
                  {userData?.displayName ? getInitials(userData.displayName) : '👤'}
                </div>
              )}
              <div>
                <h1 className="text-2xl font-bold text-ink">
                  {t('userDashboard.greeting', { name: userData?.displayName || 'Usuario' })}
                </h1>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-sm text-ink-muted">
                    {userData?.email}
                  </span>
                </div>
              </div>
            </div>

            {/* Monkey Points & Logout */}
            <div className="flex items-center gap-4">
              <div className="rounded-xl px-6 py-3 border border-accent/30/30">
                <div className="flex items-center gap-2">
                  <span className="text-3xl">🐵</span>
                  <div>
                    <div className="text-xs text-ink-muted uppercase tracking-wider">
                      {t('userDashboard.monkeyPoints')}
                    </div>
                    <div className="text-2xl font-black text-accent">
                      {userData?.monkeyPoints || 0}
                    </div>
                  </div>
                </div>
              </div>
              <button
                onClick={handleLogout}
                className="px-6 py-3 bg-surface-2 hover:bg-surface-3 text-ink rounded-lg transition-colors font-medium"
              >
                {t('userDashboard.logout')}
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-6 py-12">
        <div className="max-w-6xl mx-auto">
          {/* Dashboard Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {dashboardCards.map((card) => (
              <button
                key={card.id}
                onClick={() => navigate(card.path)}
                className="group relative bg-surface rounded-2xl p-8 border-2 border-hairline hover:border-accent/30 transition-all duration-300 hover:scale-105 hover:shadow-2xl"
              >
                <div className={`absolute inset-0 ${card.gradient} opacity-0 group-hover:opacity-10 rounded-2xl transition-opacity duration-300`}></div>

                <div className="relative">
                  <div className="text-6xl mb-4">{card.icon}</div>
                  <h3 className="text-xl font-bold text-ink mb-2">
                    {card.title}
                  </h3>
                  <p className="text-sm text-ink-muted">
                    {card.description}
                  </p>
                </div>
              </button>
            ))}
          </div>

          {/* Next Appointment Section (if exists) */}
          <div className="mt-12 bg-surface rounded-2xl p-8 border border-hairline">
            <h2 className="text-2xl font-bold text-ink mb-4 flex items-center gap-2">
              <span>📅</span>
              {t('userDashboard.nextAppointment.title')}
            </h2>
            <div className="text-center py-12">
              <div className="text-5xl mb-4">🎯</div>
              <p className="text-ink-muted">
                {t('userDashboard.nextAppointment.empty')}
              </p>
              <button
                onClick={() => navigate('/take-ticket')}
                className="mt-6 text-ink font-bold px-8 py-3 rounded-lg shadow-lg transition-all"
              >
                {t('userDashboard.nextAppointment.takeTicketNow')}
              </button>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
